import { GoogleLoginProvider, SocialAuthServiceConfig, SocialLoginModule } from '@abacritt/angularx-social-login';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgIdleModule } from '@ng-idle/core';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ClipboardModule } from 'ngx-clipboard';
import { NgxFileDropModule } from 'ngx-file-drop';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { HomeNewRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { RegulatedInterceptor } from './core/interceptors/regulated.interceptor';
import { DatePipe, LocationStrategy } from '@angular/common';
import { CommunityLocationStrategy } from './core/services/co-brand.service';
import { NgxDaterangepickerBootstrapModule } from 'ngx-daterangepicker-bootstrap';
import { HeaderNewComponent } from './shared/components/header-new/header-new.component';
import { FooterNewComponent } from './shared/components/footer-new/footer-new.component';
import { AllowanceWarningModalComponent } from './shared/components/allowance-warning-modal/allowance-warning-modal.component';
import { CopyDirective } from './shared/directives/copy.directive';
import { ContractUtils } from './shared/utils/contract-utils';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ErrorLoggerService } from './core/services/error-logs.service';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { UpdateNavigationService } from './core/services/update-navigation.service';
import { AuthReturnUrlService } from './shared/services/auth-return-url.service';
import { ProcessingInterceptor } from './core/interceptors/processing.interceptor';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { CacheInterceptor } from './core/interceptors/cache.interceptor';
import { RetryInterceptor } from './core/interceptors/retry.interceptor';
import { TimeoutInterceptor } from './core/interceptors/timeout.interceptor';
import { ServiceWorkerBypassInterceptor } from './core/interceptors/service-worker-bypass.interceptor';
import { LoggerService } from './shared/services/logger.service';
import { AuthService } from './shared/services/auth.service';
import { TelegramErrorReporterService } from './core/services/telegram-error-reporter.service';
import { TrackedToastrService } from './core/services/tracked-toastr.service';

/**
 * The service worker is deliberately OFF on the local dev server, because its caching
 * fights `ng serve`. That also means the deploy auto-refresh flow cannot be exercised
 * locally, so this allows an explicit, per-browser opt-in:
 *
 *   localStorage.setItem('rw:sw-local', '1')   // then reload
 *
 * Only affects localhost/127.0.0.1, and only for whoever sets the key. Every deployed
 * host is unchanged. NOTE: `ng serve` never emits ngsw-worker.js/ngsw.json at all, so a
 * local test still needs a real `ng build` served statically over http://localhost.
 */
function isServiceWorkerEnabled(): boolean {
    const host = window.location.hostname;
    const isLocalHost = host.includes('localhost') || host.includes('127.0.0.1');
    if (!isLocalHost) {
        return true;
    }
    try {
        return localStorage.getItem('rw:sw-local') === '1';
    } catch {
        // Storage blocked (private mode / site-data off) — stay disabled locally.
        return false;
    }
}

function initializeAuthSession(authService: AuthService) {
    return () => authService.initializeSession();
}

function installErrorListeners(reporter: TelegramErrorReporterService) {
    return () => reporter.installGlobalListeners();
}

function startUpdateNavigation(updateNavigation: UpdateNavigationService) {
    return () => updateNavigation.start();
}

function startAuthReturnUrl(authReturnUrl: AuthReturnUrlService) {
    return () => authReturnUrl.start();
}
@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [BrowserModule,
        // Only what the root template renders. Feature components import their own
        // dependencies, so they stay in their lazy chunks instead of main.js.
        HeaderNewComponent,
        FooterNewComponent,
        AllowanceWarningModalComponent,
        NgxDaterangepickerBootstrapModule.forRoot(),
        HomeNewRoutingModule,
        ModalModule,
        ReactiveFormsModule,
        FormsModule,
        CarouselModule,
        BrowserAnimationsModule,
        NgxFileDropModule,
        ToastrModule.forRoot({
            positionClass: 'toast-top-center',
            timeOut: 10000,
            closeButton: true,
            preventDuplicates: true
        }),
        SocialLoginModule,
        ClipboardModule,
        NgIdleModule.forRoot(),
        NgxSpinnerModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            // Enabled on every deployed host (dev/staging/prod) so the update
            // flow works across environments; on localhost it is opt-in only —
            // see isServiceWorkerEnabled() above.
            enabled: isServiceWorkerEnabled(),
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        })], providers: [
        // Root-level injectables that lazy components inject.
        CopyDirective,
        DatePipe,
        { provide: ErrorHandler, useClass: ErrorLoggerService },
        // keeps ?community_id= in the address bar on every page of a community (core/services/co-brand.service.ts)
        { provide: LocationStrategy, useClass: CommunityLocationStrategy },
        /**
         * ORDER IS THE DESIGN. The DI array order is the request order; the response path
         * runs in reverse, so an earlier entry WRAPS a later one:
         *
         *   Regulated -> Cache -> Error -> Retry -> Timeout -> Processing -> Auth -> SwBypass
         *
         * - Cache above Processing  : a cache hit costs no spinner flash and no alert.
         * - Error OUTSIDE Retry     : only the terminal failure is reported to Telegram,
         *                             so N attempts no longer mean N alerts.
         * - Retry OUTSIDE Timeout   : every attempt is independently bounded.
         * - Retry OUTSIDE Processing: finalize() runs per attempt, so the global spinner is
         *                             released across backoff sleeps and does not block
         *                             UpdateService.requestRecovery(), which waits on
         *                             isProcessing$ going idle.
         * - Auth innermost          : unchanged; its swallowed-401 EMPTY completes rather
         *                             than errors, so Retry correctly does not fire.
         * - SwBypass innermost      : only tags FormData uploads with ?ngsw-bypass so iOS
         *                             Safari's service worker cannot drop the file body.
         */
        { provide: HTTP_INTERCEPTORS, useClass: RegulatedInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: RetryInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ProcessingInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        { provide: HTTP_INTERCEPTORS, useClass: ServiceWorkerBypassInterceptor, multi: true },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAuthSession,
            deps: [AuthService],
            multi: true
        },
        // Window-level error/rejection listeners, installed before any feature code runs.
        {
            provide: APP_INITIALIZER,
            useFactory: installErrorListeners,
            deps: [TelegramErrorReporterService],
            multi: true
        },
        // Nothing else injects UpdateNavigationService (it renders no UI), so this
        // initializer is what starts deploy detection and the auto-apply flow.
        {
            provide: APP_INITIALIZER,
            useFactory: startUpdateNavigation,
            deps: [UpdateNavigationService],
            multi: true
        },
        // Started before the first navigation so it can tell a direct visit to /sign-in
        // apart from a "Log in" click, and remember the page to return to after login.
        {
            provide: APP_INITIALIZER,
            useFactory: startAuthReturnUrl,
            deps: [AuthReturnUrlService],
            multi: true
        },
        // Every `toastr.error(...)` in the app is mirrored to the alert channel. Overriding
        // the root ToastrService covers all existing call sites without editing any of them.
        { provide: ToastrService, useClass: TrackedToastrService },
        {
            provide: 'SocialAuthServiceConfig',
            deps: [LoggerService],
            useFactory: (logger: LoggerService) => ({
                autoLogin: false,
                providers: [
                    {
                        id: GoogleLoginProvider.PROVIDER_ID,
                        provider: new GoogleLoginProvider(environment.GOOGLE_LOGIN_PROVIDER, {
                            oneTapEnabled: false
                        })
                    }
                ],
                onError: (err: unknown) => {
                    logger.error('Social auth provider error', err);
                }
            } as SocialAuthServiceConfig),
        },
        ContractUtils,
        provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({
            cookieName: 'XSRF-TOKEN',
            headerName: 'X-XSRF-TOKEN'
        }))
    ] })
export class AppModule { }

